import { useTranslation } from 'react-i18next';
import { KoobBadge } from '@koob/ui';
import {
  actionLabel,
  resourceName,
  resourceTypeLabel,
  verbTone,
} from './labels';

const OUTCOME_COLORS = { denied: 'orange', error: 'red' };
const TONES = {
  create: { icon: 'fa-plus', className: 'bg-green-50 text-green-600' },
  delete: { icon: 'fa-trash', className: 'bg-red-50 text-red-600' },
  access: {
    icon: 'fa-right-to-bracket',
    className: 'bg-gray-100 text-gray-500',
  },
  send: { icon: 'fa-paper-plane', className: 'bg-blue-50 text-blue-600' },
  edit: { icon: 'fa-pen', className: 'bg-blue-50 text-blue-600' },
  archive: { icon: 'fa-box-archive', className: 'bg-gray-100 text-gray-600' },
  unarchive: { icon: 'fa-box-open', className: 'bg-gray-100 text-gray-600' },
  failed: {
    icon: 'fa-triangle-exclamation',
    className: 'bg-orange-50 text-orange-600',
  },
};

/** Round icon of an audit row: the kind of action, or a warning when it failed. */
export function AuditLogIcon({ row, className = '' }) {
  const tone = TONES[row.outcome === 'success' ? verbTone(row) : 'failed'];
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-white ${tone.className} ${className}`}
    >
      <i className={`fa-regular ${tone.icon} text-xs`} />
    </span>
  );
}

/**
 * "Updated User Jane Doe", with a Denied or Failed badge when it did not go
 * through. `name` replaces the resource name, a filter link for example.
 */
export function AuditLogSentence({ row, name }) {
  const { t } = useTranslation('auditLog');
  return (
    <span className="flex flex-wrap items-baseline gap-x-1.5 text-sm text-gray-900">
      <span className="font-medium">{actionLabel(t, row)}</span>
      {row.resourceType && (
        <span className="text-gray-500">
          {resourceTypeLabel(t, row.resourceType)}
        </span>
      )}
      {row.resourceId &&
        (name ?? <span className="font-medium">{resourceName(row)}</span>)}
      {row.outcome !== 'success' && (
        <KoobBadge size="sm" color={OUTCOME_COLORS[row.outcome] ?? 'gray'}>
          {t(`outcomes.${row.outcome}`)}
        </KoobBadge>
      )}
    </span>
  );
}

/** Who did it: the user, an API key, Koob support or the other organization. */
export const actorLabel = (t, row) =>
  row.actor
    ? row.actor.name || row.actor.email
    : row.apiKey
      ? t('apiKey', { name: row.apiKey.name })
      : row.koobSupport
        ? t('koobSupport')
        : // Another organization's staff: the API names only it.
          (row.organization?.name ?? t('unknownUser'));

export function ImpersonationBadge({ row }) {
  const { t } = useTranslation('auditLog');
  return row.impersonated ? (
    <KoobBadge size="sm" color="purple">
      {row.impersonator
        ? t('impersonatedBy', { name: row.impersonator.name })
        : t('koobSupport')}
    </KoobBadge>
  ) : null;
}

/** "MCP" when an AI assistant made the call through Koob's MCP server. */
export function ViaBadge({ row }) {
  const { t } = useTranslation('auditLog');
  return row.via ? (
    <KoobBadge size="sm" color="blue">
      {t(`via.${row.via}`)}
    </KoobBadge>
  ) : null;
}
